/* eslint-disable @next/next/no-img-element */
import { useState } from "react"
import timeAgo from "utils/timeAgo"
import useT from "utils/useT"
import { motion } from "framer-motion"
import { AppInterface, submitter, translatedDescription } from "types/types"
import useGlobalStore from "stores/global"
import useRouterStore from "stores/routerStore"
import imagePlaceholder from "utils/imagePlaceholder"
import Link from "next/link"
import useSessionStore from "stores/session"
import { FallbackImage } from "components/FallbackImage"
import { event } from "utils/gtag"
import Image from "next/image"
import FullCardModal from "views/AppPreview/FullCardModal"
import DeleteComment from "views/AppPreview/DeleteComment"
import AppsListItem from "components/AppsListItem"
import Reactions from "./Reactions"
import { isArabic } from "utils/hasArabic"

interface Props {
  id: string
  trackId?: number
  uid?: string
  displayName?: string
  photoURL?: string
  lastModified?: string
  submitterDescription?: string
  translatedDescription?: translatedDescription[]
  submittedApp?: AppInterface
  media?: submitter["other"]["tweet"]["media"]
  app: Partial<AppInterface>
}

const PostCard = ({
  id,
  trackId,
  uid,
  displayName,
  photoURL,
  lastModified,
  submitterDescription,
  translatedDescription,
  media,
  app,
}: Props) => {
  const t = useT()
  const [expandImage, setExpandImage] = useState("")
  const [fallbackHeight, setFallbackHeight] = useState<number | null>(null)
  const isDark = useGlobalStore((state) => state.isDark)
  const userId = useSessionStore((state) => state.user.uid)
  const isAdmin = useSessionStore((state) => state.user.isAdmin)
  const translateSubmitterNote = useGlobalStore(
    (state) => state.translateSubmitterNote,
  )
  const sameUser = uid === userId
  const animationToggle = useGlobalStore((state) => state.animationToggle)
  const locale = useRouterStore((state) => state.router?.locale)

  const defaultTranslate =
    translatedDescription &&
    translatedDescription?.find((item) => locale?.includes(item.to))
  const [translate, setTranslate] = useState(true)
  const defaultDescription =
    defaultTranslate?.text &&
    submitterDescription &&
    submitterDescription?.length / 2 > defaultTranslate?.text?.length
      ? submitterDescription
      : defaultTranslate && translateSubmitterNote
      ? defaultTranslate?.text
      : submitterDescription
  const [description, setDescription] = useState(defaultDescription)
  const formattedDate = timeAgo(new Date(lastModified || "null"))

  const changeDescription = () => {
    translate
      ? setDescription(submitterDescription)
      : setDescription(defaultTranslate?.text)
    setTranslate(!translate)
  }
  const descriptionLang =
    translatedDescription &&
    translatedDescription?.find(
      (item) =>
        item.text.replace(/\s/g, "") ===
        submitterDescription?.replace(/\s/g, ""),
    )?.to

  return (
    <>
      <motion.div
        onPointerDownCapture={(e) => e.stopPropagation()}
        layoutId={id}
        className="card card-style"
      >
        <div className="content">
          <div className="d-flex">
            <Link
              href={`/profile/${uid}`}
              onClick={(e) => {
                e.stopPropagation()
                event({
                  action: "submitter_card_photo_click",
                  category: "submitter note",
                  label: "submitter note edited",
                  value: displayName,
                })
              }}
            >
              <motion.div
                layoutId={animationToggle ? id + "photo" : undefined}
                className="me-2"
              >
                <FallbackImage
                  className="rounded-circle border border-2 border-highlight-light p-0-5"
                  src={photoURL ? photoURL : "/images/avatars/4s.png"}
                  width={45}
                  height={45}
                  alt="profile photo"
                  placeholder="blur"
                  blurDataURL={imagePlaceholder(isDark)}
                  unoptimized
                />
              </motion.div>
            </Link>
            <div className="align-self-center">
              <motion.h6
                layoutId={animationToggle ? id + "name" : undefined}
                className="mb-2 mt-1"
              >
                {displayName ? displayName : t.previewApp.submitter.guest}
              </motion.h6>
              <motion.p
                layoutId={id + "date"}
                className="font-11 mb-0 mt-n2 opacity-90"
              >
                {formattedDate ? formattedDate : null}
              </motion.p>
            </div>
          </div>
          <motion.p
            layoutId={animationToggle ? id + "description" : undefined}
            className="mt-3 word-break"
            style={{ minHeight: media ? "fit-content" : "unset" }}
            dir={isArabic(description) ? "rtl" : "auto"}
            key={description}
          >
            {description}
          </motion.p>
          <div className="d-flex gap-3 mb-3">
            {translatedDescription &&
              translateSubmitterNote &&
              !descriptionLang?.includes(locale || "") && (
                <button
                  className="chip chip-small m-0"
                  onClick={changeDescription}
                >
                  {translate ? (
                    <i className="bg-blue-dark">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 17 17"
                      >
                        <path d="M4.545 6.714 4.11 8H3l1.862-5h1.284L8 8H6.833l-.435-1.286H4.545zm1.634-.736L5.5 3.956h-.049l-.679 2.022H6.18z" />
                        <path d="M0 2a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v3h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-3H2a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H2zm7.138 9.995c.193.301.402.583.63.846-.748.575-1.673 1.001-2.768 1.292.178.217.451.635.555.867 1.125-.359 2.08-.844 2.886-1.494.777.665 1.739 1.165 2.93 1.472.133-.254.414-.673.629-.89-1.125-.253-2.057-.694-2.82-1.284.681-.747 1.222-1.651 1.621-2.757H14V8h-3v1.047h.765c-.318.844-.74 1.546-1.272 2.13a6.066 6.066 0 0 1-.415-.492 1.988 1.988 0 0 1-.94.31z" />
                      </svg>
                    </i>
                  ) : (
                    <i className="fa fa-check bg-green-dark"></i>
                  )}
                  <strong className="font-400">
                    {translate
                      ? t.previewApp.submitter.seeOriginal
                      : t.previewApp.submitter.original}
                  </strong>
                </button>
              )}
            {(isAdmin || sameUser) && (
              <>
                <DeleteComment trackId={trackId} submitterId={uid} />
                {media && (
                  <a
                    title="view tweet"
                    className="icon icon-xxs rounded-m bg-twitter color-white"
                    href={media?.[0]?.expanded_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="fab fa-twitter"></i>
                  </a>
                )}
              </>
            )}
          </div>
          {!!media?.length && (
            <div className="d-flex overflow-scroll gap-3 px-3 mx-n3 pb-2 align-items-center">
              {media?.map((item: any) => (
                <motion.div
                  layoutId={item.media_url_https}
                  key={item.media_url_https}
                  style={{
                    position: "relative",
                    width: media?.length !== 1 ? "250px" : "calc(100vw - 58px)",
                    height: fallbackHeight === 0 ? fallbackHeight : "300px",
                    maxWidth: 500,
                  }}
                >
                  <Image
                    src={item.media_url_https || imagePlaceholder(isDark)}
                    placeholder="blur"
                    blurDataURL={imagePlaceholder(isDark)}
                    unoptimized
                    alt={item.type}
                    className="rounded-sm"
                    onClick={() => setExpandImage(item.media_url_https)}
                    style={{ objectFit: "cover" }}
                    width={media?.length !== 1 ? 250 : undefined}
                    height={
                      fallbackHeight === 0
                        ? fallbackHeight
                        : media?.length !== 1
                        ? 300
                        : undefined
                    }
                    fill={media?.length === 1}
                    onError={() => setFallbackHeight(0)}
                  />
                </motion.div>
              ))}
            </div>
          )}
          <div className="mb-n2">
            <AppsListItem
              trackId={app.trackId}
              artworkUrl512={app.artworkUrl512}
              trackName={app.trackName}
              genres={app.genres}
              description={app.description}
              price={app.price}
              formattedPrice={app.formattedPrice}
              submitterPrice={app.submitterPrice}
              submitterDescription={app.submitterDescription}
              submitter={app.submitters?.[0]}
              i={app.trackId}
              appRemoved={app.appRemoved}
              hasInAppPurchases={app.hasInAppPurchases}
            />
          </div>
        </div>
        <Reactions
          trackId={app.trackId!}
          reactions={app.submitters?.[0]?.reactions}
          submitterId={app.submitters?.[0]?.uid!}
        />
      </motion.div>
      <FullCardModal
        modalOpen={!!expandImage}
        close={() => setExpandImage("")}
        disableDragUp
      >
        <div className="d-flex overflow-scroll gap-3 px-3 pb-3 align-items-center">
          {media?.map((item: any) => (
            <motion.img
              layoutId={item.media_url_https}
              key={item.media_url_https}
              src={item.media_url_https}
              alt={item.type}
              className="rounded-sm"
              onClick={() => setExpandImage(item.media_url_https)}
              style={{ maxWidth: "95%", maxHeight: "70vh" }}
            />
          ))}
        </div>
      </FullCardModal>
    </>
  )
}

export default PostCard
